import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  ValidationResult,
  TelnyxEvent,
  CallbackResult,
} from './types';
import { buildCallbackUrl } from './types';
import { getVoiceAttributes } from './voice';
import { interpolateVariables, resolveMessageWithRephrases } from './interpolate';
import type { Rephrases } from './interpolate';

export type DigitMapping = {
  id: string;
  digit: string;
  label: string;
  repeat?: boolean;
};

export type GatherConfig = {
  prompt: string | Record<string, string>;
  inputType: string;
  numDigits?: number;
  timeout: number;
  finishOnKey: string;
  digitMappings: DigitMapping[];
  handleInvalid: boolean;
  outputVariable: string;
  mode?: 'menu' | 'gather';
  repeatDigit?: string;
  retryCount?: number;
  retryPrompt?: string | Record<string, string>;
  promptRephrases?: Rephrases;
  retryPromptRephrases?: Rephrases;
};

function retryKeyFor(nodeId: string): string {
  return `_gatherRetries_${nodeId}`;
}

function generateTeXML(config: GatherConfig, context: TeXMLContext): TeXMLElement[] {
  const callbackUrl = buildCallbackUrl(context);
  const retryKey = retryKeyFor(context.nodeId);
  const retryCount = parseInt(context.variables[retryKey] || '0', 10);
  const languageCode = context.variables._language;
  const defaultLang = context.languageConfig?.defaultLanguage;
  const rawTemplate = retryCount > 0 && config.retryPrompt ? config.retryPrompt : config.prompt;
  const visitKey = `_rephrase_${context.nodeId}`;
  const visitCount = parseInt(context.variables[visitKey] || '0', 10);
  const rephrases = retryCount > 0 && config.retryPrompt ? config.retryPromptRephrases : config.promptRephrases;
  const resolved = resolveMessageWithRephrases(rawTemplate, rephrases, visitCount, languageCode, defaultLang);
  const prompt = interpolateVariables(resolved, context.variables, context.speechFormats);
  context.variables[visitKey] = String(visitCount + 1);

  const isMenu = config.mode !== 'gather';

  return [
    {
      tag: 'Gather',
      attributes: {
        input: config.inputType,
        timeout: config.timeout.toString(),
        finishOnKey: isMenu ? undefined : config.finishOnKey,
        numDigits: isMenu ? '1' : config.numDigits?.toString(),
        action: callbackUrl,
        method: 'POST',
      },
      children: prompt
        ? [
            {
              tag: 'Say',
              attributes: getVoiceAttributes(
                context.voiceConfig,
                languageCode,
                context.languageConfig,
                context.elevenLabsApiKeyRef,
              ),
              content: prompt,
            },
          ]
        : undefined,
    },
    {
      tag: 'Redirect',
      attributes: { method: 'POST' },
      content: callbackUrl,
    },
  ];
}

function buildRepromptElements(
  config: GatherConfig,
  context: TeXMLContext,
  newRetryCount: number,
  promptSource: 'retry' | 'main',
): CallbackResult {
  const callbackUrl = buildCallbackUrl(context);
  const languageCode = context.variables._language;
  const defaultLang = context.languageConfig?.defaultLanguage;
  const retryPrompt = promptSource === 'retry' ? config.retryPrompt : undefined;
  const rawTemplate = retryPrompt || config.prompt;
  const visitKey = promptSource === 'retry' ? `_rephrase_${context.nodeId}_retry` : `_rephrase_${context.nodeId}`;
  const visitCount = parseInt(context.variables[visitKey] || '0', 10);
  const rephrases = retryPrompt ? config.retryPromptRephrases : config.promptRephrases;
  const resolved = resolveMessageWithRephrases(rawTemplate, rephrases, visitCount, languageCode, defaultLang);
  const prompt = interpolateVariables(resolved, context.variables, context.speechFormats);
  const retryKey = retryKeyFor(context.nodeId);

  const isMenu = config.mode !== 'gather';

  return {
    variables: { [retryKey]: String(newRetryCount), [visitKey]: String(visitCount + 1) },
    elements: [
      {
        tag: 'Gather',
        attributes: {
          input: config.inputType,
          timeout: config.timeout.toString(),
          finishOnKey: isMenu ? undefined : config.finishOnKey,
          numDigits: isMenu ? '1' : config.numDigits?.toString(),
          action: callbackUrl,
          method: 'POST',
        },
        children: [
          {
            tag: 'Say',
            attributes: getVoiceAttributes(
              context.voiceConfig,
              languageCode,
              context.languageConfig,
              context.elevenLabsApiKeyRef,
            ),
            content: prompt,
          },
        ],
      },
      {
        tag: 'Redirect',
        attributes: { method: 'POST' },
        content: callbackUrl,
      },
    ],
  };
}

export function gatherCallbackHandler(
  event: TelnyxEvent,
  config: GatherConfig,
  variables: Record<string, string>,
  context: TeXMLContext,
): CallbackResult {
  const digits = event.Digits;
  const varName = config.outputVariable || 'digits';
  const maxRetries = config.retryCount ?? 0;
  const retryKey = retryKeyFor(context.nodeId);
  const currentRetries = parseInt(variables[retryKey] || '0', 10);

  if (config.mode === 'gather') {
    if (digits) {
      if (config.repeatDigit && digits === config.repeatDigit) {
        return buildRepromptElements(config, context, 0, 'main');
      }
      return {
        nextHandleId: 'out',
        variables: { [varName]: digits, [retryKey]: '0' },
      };
    }

    if (currentRetries < maxRetries) {
      return buildRepromptElements(config, context, currentRetries + 1, 'retry');
    }

    return { nextHandleId: 'timeout', variables: { [retryKey]: '0' } };
  }

  if (!digits) {
    if (currentRetries < maxRetries) {
      return buildRepromptElements(config, context, currentRetries + 1, 'retry');
    }
    return { nextHandleId: 'timeout', variables: { [retryKey]: '0' } };
  }

  const matchedMapping = config.digitMappings.find((m) => m.digit === digits);

  if (matchedMapping) {
    if (matchedMapping.repeat) {
      return buildRepromptElements(config, context, 0, 'main');
    }
    return {
      nextHandleId: matchedMapping.id,
      variables: { [varName]: digits, [retryKey]: '0' },
    };
  }

  if (currentRetries < maxRetries) {
    return buildRepromptElements(config, context, currentRetries + 1, 'retry');
  }

  return {
    nextHandleId: config.handleInvalid ? 'invalid' : undefined,
    variables: { [varName]: digits, [retryKey]: '0' },
  };
}

function hasContent(value: string | Record<string, string>): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  return Object.values(value).some((v) => v.trim().length > 0);
}

function validateConfig(config: GatherConfig): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!hasContent(config.prompt)) {
    errors.push('Prompt is required');
  }

  if (config.mode !== 'gather') {
    if (config.digitMappings.length === 0) {
      errors.push('At least one digit mapping is required');
    }

    const usedDigits = new Set<string>();
    config.digitMappings.forEach((mapping, index) => {
      if (usedDigits.has(mapping.digit)) {
        errors.push(`Duplicate digit "${mapping.digit}" in mapping ${index + 1}`);
      }
      usedDigits.add(mapping.digit);

      if (!mapping.repeat && !mapping.label.trim()) {
        warnings.push(`Mapping for digit "${mapping.digit}" has no label`);
      }
    });
  }

  if (config.timeout < 1 || config.timeout > 30) {
    errors.push('Timeout must be between 1 and 30 seconds');
  }

  const retryCount = config.retryCount ?? 0;
  if (retryCount < 0 || retryCount > 5) {
    errors.push('Retry count must be between 0 and 5');
  }

  if (config.mode === 'gather' && config.numDigits !== undefined && config.numDigits !== null) {
    if (config.numDigits < 1 || !Number.isInteger(config.numDigits)) {
      errors.push('Number of digits must be a positive integer');
    }
  }

  if (config.mode === 'gather' && config.repeatDigit) {
    if (config.numDigits === 1) {
      errors.push('Repeat digit cannot be used when number of digits is 1');
    }
    if (config.repeatDigit === config.finishOnKey) {
      errors.push('Repeat digit cannot be the same as the finish key');
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}

export const gatherWidgetCore: WidgetCoreDefinition<GatherConfig> = {
  id: 'gather',
  version: 1,
  name: 'Gather',
  description: 'Collect DTMF digits or speech input from caller',
  category: 'input',
  tier: 'standard',

  defaultConfig: {
    prompt: '',
    inputType: 'dtmf',
    timeout: 5,
    finishOnKey: '#',
    digitMappings: [],
    handleInvalid: false,
    outputVariable: 'digits',
    mode: 'menu',
    retryCount: 0,
    retryPrompt: '',
  },

  variables: {
    outputs: [
      {
        configKey: 'outputVariable',
        description: 'The collected digits or speech input',
        type: 'string',
        availableOnHandleTypes: ['default', 'digit'],
      },
    ],
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: true,
  },

  validation: {
    validateConfig,
  },
};
